import React, { useCallback, useEffect, useMemo, useState } from 'react';
import KpiChartPanel from '../kpi-dashboard/charts/KpiChartPanel';
import BandwidthPanelExportShell from './BandwidthPanelExportShell';
import { exportBandwidthHeatmapCountsByCustomerExcel } from './bandwidthDashboardExcelExport';
import { exportBandwidthHeatmapCountsByCustomerPdf } from './bandwidthDashboardPdfExport';
import {
  bwKpiPanelSubtitleClass,
  bwKpiTableFooterRowClass,
  bwKpiTableHeaderCellClass,
  bwKpiTableHeaderClass,
  bwKpiTableHeaderFirstColClass,
  bwKpiTableBodyFirstColClassWithBg,
  bwKpiTableSelectedRowClass,
} from './bandwidthKpiTableChrome';
import { fmtBwAvg, fmtBwCount, fmtBwDecimal, fmtBwMinutes, fmtBwPercent, roundBwDecimal } from './bandwidthFormat';
import { getBandwidthUtilizationPct } from './bandwidthUtilization';

type SortDir = 'asc' | 'desc';
type SortKey = 'customer' | 'totalActivity' | 'timeMinutes' | 'timeHrs' | 'avgHrs' | 'utilization';

function sortIndicator(active: boolean, dir: SortDir): string {
  if (!active) return '↕';
  return dir === 'desc' ? '↓' : '↑';
}

function ariaSortForColumn(active: boolean, dir: SortDir): 'ascending' | 'descending' | 'none' {
  if (!active) return 'none';
  return dir === 'asc' ? 'ascending' : 'descending';
}

/** Flat counts table for the classic Dashboard (heatmap) tab — not the hierarchical Count & Time table. */
export interface HeatmapCountsByCustomerRow {
  customer: string;
  customerId?: string;
  /** Customer Type label: 'CSP' | 'Non CSP'. */
  customerType?: string | null;
  totalActivity: number;
  timeMinutes: number;
  timeHrs: number;
  avgHrsOnActivity: number | null;
}

export interface BandwidthHeatmapCountsTableProps {
  title: string;
  subtitle?: string;
  rows: HeatmapCountsByCustomerRow[];
  selectedCustomerId?: string | null;
  onRowClick?: (row: HeatmapCountsByCustomerRow) => void;
  loading?: boolean;
  errorMessage?: string | null;
  /** When set, shows Export → PDF / Excel using the same sort order as the table. */
  exportFilePrefix?: string;
  exportDateFrom?: string;
  exportDateTo?: string;
}

function customerUtilizationSortRatio(row: HeatmapCountsByCustomerRow, totalHrs: number): number {
  return totalHrs > 0 ? row.timeHrs / totalHrs : -1;
}

/** Share of total recorded hours across all customers in the table. */
function customerUtilizationPct(row: HeatmapCountsByCustomerRow, totalHrs: number): number | null {
  return getBandwidthUtilizationPct(row.timeHrs, totalHrs);
}

const BandwidthHeatmapCountsTable: React.FC<BandwidthHeatmapCountsTableProps> = ({
  title,
  subtitle,
  rows,
  selectedCustomerId = null,
  onRowClick,
  loading = false,
  errorMessage = null,
  exportFilePrefix,
  exportDateFrom = '',
  exportDateTo = '',
}) => {
  const [sortKey, setSortKey] = useState<SortKey>('timeHrs');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  const toggleSort = useCallback((key: SortKey) => {
    if (sortKey === key) {
      setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortKey(key);
      setSortDir(key === 'customer' ? 'asc' : 'desc');
    }
  }, [sortKey]);

  const rowsIdentity = rows.map((r) => `${r.customerId ?? r.customer}`).join('|');
  useEffect(() => {
    setSortKey('timeHrs');
    setSortDir('desc');
  }, [rowsIdentity]);

  const totalHrs = useMemo(() => rows.reduce((sum, r) => sum + r.timeHrs, 0), [rows]);

  const sortedRows = useMemo(() => {
    const dirMul = sortDir === 'desc' ? -1 : 1;
    return [...rows].sort((a, b) => {
      if (sortKey === 'customer') {
        const c = a.customer.localeCompare(b.customer, undefined, { sensitivity: 'base' });
        return sortDir === 'asc' ? c : -c;
      }
      if (sortKey === 'totalActivity') return dirMul * (a.totalActivity - b.totalActivity);
      if (sortKey === 'timeMinutes') return dirMul * (a.timeMinutes - b.timeMinutes);
      if (sortKey === 'timeHrs') return dirMul * (a.timeHrs - b.timeHrs);
      if (sortKey === 'utilization') {
        return (
          dirMul *
          (customerUtilizationSortRatio(a, totalHrs) - customerUtilizationSortRatio(b, totalHrs))
        );
      }
      const avA = a.avgHrsOnActivity ?? -1;
      const avB = b.avgHrsOnActivity ?? -1;
      return dirMul * (avA - avB);
    });
  }, [rows, sortKey, sortDir, totalHrs]);

  const totals = rows.reduce(
    (acc, r) => ({
      count: acc.count + r.totalActivity,
      min: acc.min + r.timeMinutes,
      hrs: acc.hrs + r.timeHrs,
    }),
    { count: 0, min: 0, hrs: 0 }
  );
  const totalAvgHrs =
    totals.count > 0 ? roundBwDecimal(totals.hrs / totals.count) : null;
  const totalUtilPct = getBandwidthUtilizationPct(totals.hrs, totals.hrs);

  const sortHeadersDisabled = loading || Boolean(errorMessage) || rows.length === 0;

  const exportRows = useMemo(
    () =>
      sortedRows.map((r) => ({
        customer: r.customer,
        customerType: r.customerType ?? null,
        totalActivity: r.totalActivity,
        timeMinutes: r.timeMinutes,
        timeHrs: r.timeHrs,
        avgHrsOnActivity: r.avgHrsOnActivity,
        utilizationPct: customerUtilizationPct(r, totalHrs),
      })),
    [sortedRows, totalHrs]
  );

  const exportDisabled =
    loading || Boolean(errorMessage) || rows.length === 0 || !exportFilePrefix;

  const titleEnd = exportFilePrefix ? (
    <BandwidthPanelExportShell
      disabled={exportDisabled}
      ariaLabel={`Export ${title}`}
      buildPdf={() =>
        exportBandwidthHeatmapCountsByCustomerPdf(
          exportRows,
          `Bandwidth dashboard — ${title}`,
          exportFilePrefix,
          exportDateFrom,
          exportDateTo
        )
      }
      buildExcel={() => exportBandwidthHeatmapCountsByCustomerExcel(exportRows, exportFilePrefix, exportDateFrom, exportDateTo)}
    />
  ) : undefined;

  return (
    <KpiChartPanel
      title={title}
      titleClassName="!pl-0"
      titleEnd={titleEnd}
      className="h-full min-h-0 max-h-[400px]"
      flexFill
      bodyClassName="px-0 pb-0 pt-0"
    >
      {subtitle ? (
        <p className={bwKpiPanelSubtitleClass}>{subtitle}</p>
      ) : null}
      <div className="min-h-0 flex-1 overflow-auto">
        <table className="w-full min-w-[120px] border-collapse text-left text-xs">
          <thead>
            <tr className={bwKpiTableHeaderClass}>
              <th
                scope="col"
                aria-sort={ariaSortForColumn(sortKey === 'customer', sortDir)}
                className={`min-w-[120px] px-3 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wide ${bwKpiTableHeaderCellClass} ${bwKpiTableHeaderFirstColClass}`}
              >
                <button
                  type="button"
                  disabled={sortHeadersDisabled}
                  onClick={() => toggleSort('customer')}
                  className="inline-flex items-center gap-1 rounded px-0.5 text-left font-semibold text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
                >
                  Customer
                  <span className="font-normal opacity-90" aria-hidden>
                    {sortIndicator(sortKey === 'customer', sortDir)}
                  </span>
                </button>
              </th>
              <th
                scope="col"
                aria-sort={ariaSortForColumn(sortKey === 'totalActivity', sortDir)}
                className={`min-w-[120px] px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide ${bwKpiTableHeaderCellClass}`}
              >
                <button
                  type="button"
                  disabled={sortHeadersDisabled}
                  onClick={() => toggleSort('totalActivity')}
                  className="inline-flex w-full items-center justify-end gap-1 rounded px-0.5 font-semibold text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
                >
                  Total Activity Count
                  <span className="font-normal opacity-90" aria-hidden>
                    {sortIndicator(sortKey === 'totalActivity', sortDir)}
                  </span>
                </button>
              </th>
              <th
                scope="col"
                aria-sort={ariaSortForColumn(sortKey === 'timeMinutes', sortDir)}
                className={`min-w-[120px] px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide ${bwKpiTableHeaderCellClass}`}
              >
                <button
                  type="button"
                  disabled={sortHeadersDisabled}
                  onClick={() => toggleSort('timeMinutes')}
                  className="inline-flex w-full items-center justify-end gap-1 rounded px-0.5 font-semibold text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
                >
                  Time Spent (Minutes)
                  <span className="font-normal opacity-90" aria-hidden>
                    {sortIndicator(sortKey === 'timeMinutes', sortDir)}
                  </span>
                </button>
              </th>
              <th
                scope="col"
                aria-sort={ariaSortForColumn(sortKey === 'timeHrs', sortDir)}
                className={`min-w-[120px] px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide ${bwKpiTableHeaderCellClass}`}
              >
                <button
                  type="button"
                  disabled={sortHeadersDisabled}
                  onClick={() => toggleSort('timeHrs')}
                  className="inline-flex w-full items-center justify-end gap-1 rounded px-0.5 font-semibold text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
                >
                  Time Spent (Hrs)
                  <span className="font-normal opacity-90" aria-hidden>
                    {sortIndicator(sortKey === 'timeHrs', sortDir)}
                  </span>
                </button>
              </th>
              <th
                scope="col"
                aria-sort={ariaSortForColumn(sortKey === 'avgHrs', sortDir)}
                className={`min-w-[120px] px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide ${bwKpiTableHeaderCellClass}`}
              >
                <button
                  type="button"
                  disabled={sortHeadersDisabled}
                  onClick={() => toggleSort('avgHrs')}
                  className="inline-flex w-full items-center justify-end gap-1 rounded px-0.5 font-semibold text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
                >
                  Avg Hrs / Activity
                  <span className="font-normal opacity-90" aria-hidden>
                    {sortIndicator(sortKey === 'avgHrs', sortDir)}
                  </span>
                </button>
              </th>
              <th
                scope="col"
                aria-sort={ariaSortForColumn(sortKey === 'utilization', sortDir)}
                className={`min-w-[100px] px-3 py-2.5 text-right text-[11px] font-semibold uppercase tracking-wide ${bwKpiTableHeaderCellClass}`}
              >
                <button
                  type="button"
                  disabled={sortHeadersDisabled}
                  onClick={() => toggleSort('utilization')}
                  className="inline-flex w-full items-center justify-end gap-1 rounded px-0.5 font-semibold text-white hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white/80 disabled:cursor-not-allowed disabled:no-underline disabled:opacity-60"
                >
                  Utilization %
                  <span className="font-normal opacity-90" aria-hidden>
                    {sortIndicator(sortKey === 'utilization', sortDir)}
                  </span>
                </button>
              </th>
            </tr>
          </thead>
          <tbody className="text-slate-800">
            {errorMessage ? (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-sm text-red-700">
                  {errorMessage}
                </td>
              </tr>
            ) : loading ? (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-10 text-center text-slate-500">
                  No approved time entries for this filter.
                </td>
              </tr>
            ) : (
              <>
                {sortedRows.map((r, ri) => {
                  const rowId = r.customerId ?? r.customer;
                  const selected = selectedCustomerId != null && r.customerId === selectedCustomerId;
                  const clickable = Boolean(onRowClick && !loading && !errorMessage);
                  return (
                    <tr
                      key={rowId}
                      className={`border-b border-slate-100 transition-colors ${
                        selected
                          ? `${bwKpiTableSelectedRowClass} cursor-pointer hover:bg-indigo-100/80`
                          : `odd:bg-white even:bg-indigo-50/[0.1]${clickable ? ' cursor-pointer hover:bg-indigo-50/70' : ''}`
                      }`}
                      onClick={
                        clickable
                          ? () => {
                              onRowClick!(r);
                            }
                          : undefined
                      }
                      onKeyDown={
                        clickable
                          ? (e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onRowClick!(r);
                              }
                            }
                          : undefined
                      }
                      tabIndex={clickable ? 0 : undefined}
                      role={clickable ? 'button' : undefined}
                    >
                      <td className={`min-w-[120px] max-w-[200px] px-3 py-2 font-medium leading-snug ${bwKpiTableBodyFirstColClassWithBg({ rowIndex: ri, selected })}`}>{r.customer}</td>
                      <td className="min-w-[120px] px-3 py-2 tabular-nums">{fmtBwCount(r.totalActivity)}</td>
                      <td className="min-w-[120px] px-3 py-2 tabular-nums">{fmtBwMinutes(r.timeMinutes)}</td>
                      <td className="min-w-[120px] px-3 py-2 tabular-nums">{fmtBwDecimal(r.timeHrs)}</td>
                      <td className="min-w-[120px] px-3 py-2 tabular-nums">
                        {r.avgHrsOnActivity != null ? fmtBwAvg(r.avgHrsOnActivity) : '-'}
                      </td>
                      <td className="min-w-[100px] px-3 py-2 text-right tabular-nums">
                        {fmtBwPercent(customerUtilizationPct(r, totalHrs))}
                      </td>
                    </tr>
                  );
                })}
                <tr className={bwKpiTableFooterRowClass}>
                  <td className={`min-w-[120px] px-3 py-2.5 ${bwKpiTableBodyFirstColClassWithBg({ footer: true })}`}>Total</td>
                  <td className="min-w-[120px] px-3 py-2.5 tabular-nums">{fmtBwCount(totals.count)}</td>
                  <td className="min-w-[120px] px-3 py-2.5 tabular-nums">{fmtBwMinutes(totals.min)}</td>
                  <td className="min-w-[120px] px-3 py-2.5 tabular-nums">{fmtBwDecimal(totals.hrs)}</td>
                  <td className="min-w-[120px] px-3 py-2.5 tabular-nums">
                    {totalAvgHrs != null ? fmtBwAvg(totalAvgHrs) : '-'}
                  </td>
                  <td className="min-w-[100px] px-3 py-2.5 text-right tabular-nums">{fmtBwPercent(totalUtilPct)}</td>
                </tr>
              </>
            )}
          </tbody>
        </table>
      </div>
    </KpiChartPanel>
  );
};

export default BandwidthHeatmapCountsTable;
